import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsx } from 'react/jsx-runtime';
import { useState, useCallback, useMemo } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { ReorderAscendingIcon, ReorderDescendingIcon, ReorderDefaultIcon } from '@cognite/cogs-icons';
import { getDefaultFilterModel } from './utils.js';

/**
 *
 * @param {UseDataGridHeaderCellParams} {
 *  colDef,
 * filterModel,
 * onFilterModelChange,
 * }
 * @returns Multiple functions and states to handle the header cell
 */
var useDataGridHeaderCell = function useDataGridHeaderCell(_ref) {
  var colDef = _ref.colDef,
    filterModel = _ref.filterModel,
    onFilterModelChange = _ref.onFilterModelChange;
  var field = colDef.field,
    filter = colDef.filter,
    sortable = colDef.sortable;
  var apiRef = useGridApiContext();
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    anchorEl = _useState2[0],
    setAnchorEl = _useState2[1];
  var handleHideColumn = useCallback(function () {
    apiRef.current.setColumnVisibility(field, false);
  }, [apiRef, field]);
  // Memoized Filter Model Logic
  var filterModelExists = useMemo(function () {
    var _a, _b;
    return (_b = (_a = apiRef.current.state.filter) === null || _a === undefined ? undefined : _a.filterModel) === null || _b === undefined ? undefined : _b.items.find(function (filter) {
      return filter.field === field;
    });
    // If I set the apiRef as a dependency, this will not pick up the changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef.current.state.filter, field]);
  var columnfilterModel = useMemo(function () {
    return (filterModel === null || filterModel === undefined ? undefined : filterModel.items.find(function (filter) {
      return filter.field === field;
    })) || getDefaultFilterModel(field || 'default');
  }, [filterModel, field]);
  // Handle filter changes
  var handleOnFilter = useCallback(function (resp) {
    var existingFilter = ((filterModel === null || filterModel === undefined ? undefined : filterModel.items) || []).find(function (filter) {
      return filter.field === resp.field;
    });
    var updatedFilter = existingFilter ? ((filterModel === null || filterModel === undefined ? undefined : filterModel.items) || []).filter(function (filter) {
      return filter.field !== resp.field || resp.value.length > 0;
    }).map(function (filter) {
      if (filter.field === resp.field) {
        return {
          field: filter.field,
          value: resp.value,
          operator: resp.operator
        };
      }
      return filter;
    }) : [].concat(_toConsumableArray((filterModel === null || filterModel === undefined ? undefined : filterModel.items) || []), [{
      field: resp.field,
      value: resp.value,
      operator: resp.operator
    }]);
    onFilterModelChange === null || onFilterModelChange === undefined ? undefined : onFilterModelChange(Object.assign(Object.assign({}, filterModel), {
      items: updatedFilter
    }), {
      reason: 'upsertFilterItem'
    });
  }, [filterModel, onFilterModelChange]);
  // Handle sorting logic
  var handleSort = useCallback(function (sort) {
    var currentSortModel = apiRef.current.getSortModel();
    var currentSort = currentSortModel.find(function (sort) {
      return sort.field === field;
    });
    var newSortModel;
    if (!currentSort) {
      newSortModel = [{
        field: field,
        sort: 'asc'
      }];
    } else if (currentSort.sort === 'asc') {
      newSortModel = [{
        field: field,
        sort: 'desc'
      }];
    } else {
      newSortModel = [];
    }
    apiRef.current.setSortModel(sort ? [{
      field: field,
      sort: sort
    }] : newSortModel);
  }, [apiRef, field]);
  // Get current sorting direction
  var getSortDirection = useCallback(function () {
    var currentSortModel = apiRef.current.getSortModel();
    var currentSort = currentSortModel.find(function (sort) {
      return sort.field === field;
    });
    if ((currentSort === null || currentSort === undefined ? undefined : currentSort.sort) === 'asc') {
      return {
        icon: jsx(ReorderAscendingIcon, {}),
        'aria-label': 'Sort default'
      };
    } else if ((currentSort === null || currentSort === undefined ? undefined : currentSort.sort) === 'desc') return {
      icon: jsx(ReorderDescendingIcon, {}),
      'aria-label': 'Ascending sort'
    };
    return {
      icon: jsx(ReorderDefaultIcon, {}),
      'aria-label': 'Descending sort'
    };
  }, [apiRef, field]);
  // Menu handling
  var handleMenuOpen = useCallback(function (event) {
    setAnchorEl(event.currentTarget);
  }, []);
  var handleMenuClose = useCallback(function () {
    setAnchorEl(null);
  }, []);
  // Return the hook data
  return {
    filterModelExists: filterModelExists,
    columnfilterModel: columnfilterModel,
    handleOnFilter: handleOnFilter,
    handleSort: handleSort,
    getSortDirection: getSortDirection,
    handleMenuOpen: handleMenuOpen,
    handleHideColumn: handleHideColumn,
    handleMenuClose: handleMenuClose,
    anchorEl: anchorEl,
    filter: filter,
    sortable: sortable
  };
};

export { useDataGridHeaderCell };
